/**
 * useAutoSave — TanStack-Query-backed save mutation with optimistic UI
 * (Block 6.B.1 / ADR-024 D-edit-mutation-strategy).
 *
 * Wraps useMutation with:
 *   - Optimistic update: writes new value to cache immediately on
 *     mutate; rolls back on error (Q2 disposition: optimistic by
 *     default per ADR-017 cross-cutting #1).
 *   - Cache invalidation on success: forces refetch so cross-field
 *     server-side derived values update.
 *   - Undo entry creation on success.
 *   - Saving state exposed for inline indicator.
 *
 * Caller provides:
 *   - mutationFn: API call to save (PATCH).
 *   - onOptimisticUpdate: writer that mutates cache for the optimistic
 *     view. Receives previous data + new value; returns next data.
 *   - undoMutationFn (optional): API call that REVERSES the change.
 *     If omitted, no undo entry is created.
 *   - undoLabel (optional): label for the undo toast.
 *   - queryKey: cache key for the resource being edited.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { useUndo } from '@/lib/undo';
import { invalidateAdminReviewKeys } from '@/lib/adminReview';

export interface UseAutoSaveOptions<TData, TValue, TMutationResult = TData> {
  /** Cache key for the resource being edited (used for invalidation + optimistic update). */
  queryKey: QueryKey;
  /**
   * Save the new value. Resolves with the server response. The result
   * itself is NOT written back to the cache (onSettled invalidates the
   * queryKey + the next refetch supplies the canonical shape); only the
   * onOptimisticUpdate path writes to cache. As a result, the mutation
   * result type can diverge from the cache shape — Phase 1.10's
   * assignment-mutation endpoints return `{ display_name }` /
   * `{ display_names }` envelopes while the cache holds full
   * `ProviderDetail`, and that's fine.
   */
  mutationFn: (newValue: TValue) => Promise<TMutationResult>;
  /**
   * Optimistic-cache-update callback. Receives the cache snapshot +
   * the new value; returns the next snapshot. Implementations should
   * return undefined to skip optimistic update (e.g. when prev is
   * itself undefined — TanStack Query will pick up the result in
   * onSuccess).
   */
  onOptimisticUpdate?: (prev: TData | undefined, newValue: TValue) => TData | undefined;
  /** Save call that REVERSES the change; if provided, an undo entry is pushed on success. */
  undoMutationFn?: (priorValue: TValue) => Promise<TMutationResult>;
  /** Label for the undo toast (e.g. "Phone updated"). Required if undoMutationFn provided. */
  undoLabel?: string;
}

export interface UseAutoSaveResult<TValue> {
  save: (newValue: TValue, priorValue: TValue) => Promise<void>;
  isSaving: boolean;
  error: Error | null;
  reset: () => void;
}

export function useAutoSave<TData, TValue, TMutationResult = TData>(
  options: UseAutoSaveOptions<TData, TValue, TMutationResult>,
): UseAutoSaveResult<TValue> {
  const queryClient = useQueryClient();
  const { push: pushUndo } = useUndo();

  // The caller's options (its save call, cache key, optimistic update and
  // undo) are read when save() is called and travel in the variables, so
  // a save made just after a re-render uses that render's options, never
  // the ones React Query was handed an effect earlier.
  const mutation = useMutation<
    TMutationResult,
    Error,
    { newValue: TValue; priorValue: TValue; opts: UseAutoSaveOptions<TData, TValue, TMutationResult> },
    { previous: TData | undefined; priorValue: TValue }
  >({
    mutationFn: ({ newValue, opts }) => opts.mutationFn(newValue),
    onMutate: async ({ newValue, priorValue, opts }) => {
      // Cancel in-flight queries so they don't overwrite the optimistic update.
      await queryClient.cancelQueries({ queryKey: opts.queryKey });
      const previous = queryClient.getQueryData<TData>(opts.queryKey);
      if (opts.onOptimisticUpdate) {
        const next = opts.onOptimisticUpdate(previous, newValue);
        if (next !== undefined) {
          queryClient.setQueryData<TData>(opts.queryKey, next);
        }
      }
      return { previous, priorValue };
    },
    onError: (_err, { opts }, context) => {
      // Roll back to the snapshot taken in onMutate.
      if (context?.previous !== undefined) {
        queryClient.setQueryData<TData>(opts.queryKey, context.previous);
      }
    },
    onSuccess: (_data, { opts }, context) => {
      // Push undo entry if the caller wired one.
      if (opts.undoMutationFn && opts.undoLabel && context) {
        const undoFn = opts.undoMutationFn;
        const priorValue = context.priorValue;
        pushUndo({
          label: opts.undoLabel,
          undo: async () => {
            // Note: undo invocation does NOT push another undo entry
            // (would create an undo-redo loop). It bypasses useAutoSave
            // and calls the reversal directly; cache invalidation below
            // forces a fresh read.
            await undoFn(priorValue);
            await queryClient.invalidateQueries({ queryKey: opts.queryKey });
          },
        });
      }
    },
    onSettled: (_data, _err, { opts }) => {
      void queryClient.invalidateQueries({ queryKey: opts.queryKey });
      // Block 6.F.5 §7.8: every direct edit emits a change_log row that
      // surfaces in the admin-review timeline. Invalidate timeline +
      // badge-count keys so steward sees fresh state.
      void invalidateAdminReviewKeys(queryClient);
    },
  });

  return {
    save: (newValue, priorValue) =>
      mutation.mutateAsync({ newValue, priorValue, opts: options }).then(() => undefined),
    isSaving: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
